// ============================================================================
// SHAREDBANNERMANAGER.JS - VERSION: 2026.01.12.1800
// ============================================================================
// Admin component for managing shared/generic banners
// UPDATES: Added edit sponsor name, delete sponsor button, improved error handling
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Plus, Save, Library, ExternalLink, Edit2, X } from 'lucide-react';

const API_URL = 'https://api.traktag.com/api';

const BANNER_TYPES = [
    { key: 'desktop', label: '🖥️ Desktop', size: '728×90' },
    { key: 'mobile', label: '📱 Mobile', size: '320×50' },
    { key: 'static', label: '📺 Static', size: '300×250' },
    { key: 'exit', label: '🚪 Exit', size: '300×600' },
    { key: 'interstitial', label: '🎬 Interstitial', size: '320×480' }
];

const SharedBannerManager = () => {
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newSponsorName, setNewSponsorName] = useState('');
    const [newAffiliateUrl, setNewAffiliateUrl] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [uploading, setUploading] = useState({});
    const [deleting, setDeleting] = useState({});
    const [editingAffiliate, setEditingAffiliate] = useState(null);
    const [editingName, setEditingName] = useState(null);
    const [deletingSponsor, setDeletingSponsor] = useState(null);
    const [error, setError] = useState(null);
    
    const fileInputRefs = useRef({});

    useEffect(() => {
        loadSponsors();
    }, []);

    const loadSponsors = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            setSponsors(data.sponsors || []);
        } catch (err) {
            console.error('Failed to load sponsors:', err);
            setError(`Failed to load sponsors: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (sponsorSlug, bannerType, file, uploadKey) => {
        if (!file) return;

        setUploading(prev => ({ ...prev, [uploadKey]: true }));
        setError(null);

        try {
            const formData = new FormData();
            formData.append('banner', file);
            formData.append('sponsor', sponsorSlug);
            formData.append('bannerType', bannerType);

            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const result = await response.json();
            if (result.success) {
                loadSponsors();
            } else {
                setError('Upload failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Upload error: ' + err.message);
        } finally {
            setUploading(prev => ({ ...prev, [uploadKey]: false }));
            if (fileInputRefs.current[uploadKey]) {
                fileInputRefs.current[uploadKey].value = '';
            }
        }
    };

    const handleDeleteBanner = async (sponsorSlug, bannerType) => {
        if (!window.confirm(`Delete ${bannerType} banner for ${sponsorSlug}?`)) return;

        const deleteKey = `${sponsorSlug}-${bannerType}`;
        setDeleting(prev => ({ ...prev, [deleteKey]: true }));

        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/${bannerType}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                loadSponsors();
            } else {
                setError('Delete failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Delete error: ' + err.message);
        } finally {
            setDeleting(prev => ({ ...prev, [deleteKey]: false }));
        }
    };

    const handleDeleteSponsor = async (sponsorSlug, displayName) => {
        if (!window.confirm(`Delete "${displayName}" and ALL its banners? This cannot be undone.`)) return;

        setDeletingSponsor(sponsorSlug);

        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                loadSponsors();
            } else {
                setError('Delete sponsor failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Delete sponsor error: ' + err.message);
        } finally {
            setDeletingSponsor(null);
        }
    };

    const handleAddSponsor = async () => {
        if (!newSponsorName.trim()) {
            setError('Please enter a sponsor name');
            return;
        }

        try {
            const token = localStorage.getItem('admin_token');
            const sponsorSlug = newSponsorName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/affiliate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    affiliateUrl: newAffiliateUrl,
                    displayName: newSponsorName.trim()
                })
            });

            if (response.ok) {
                setNewSponsorName('');
                setNewAffiliateUrl('');
                setShowAddForm(false);
                loadSponsors();
            } else {
                const result = await response.json();
                setError('Failed to add sponsor: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Failed to add sponsor: ' + err.message);
        }
    };

    const updateSponsorName = async (sponsorSlug, newName) => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/affiliate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ displayName: newName })
            });
            
            if (response.ok) {
                setEditingName(null);
                loadSponsors();
            } else {
                const result = await response.json();
                setError('Failed to update name: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Failed to update name: ' + err.message);
        }
    };

    const updateAffiliateUrl = async (sponsorSlug, url) => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/affiliate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ affiliateUrl: url })
            });
            
            if (response.ok) {
                setEditingAffiliate(null);
                loadSponsors();
            } else {
                const result = await response.json();
                setError('Failed to update affiliate URL: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Failed to update affiliate URL: ' + err.message);
        }
    };

    return (
        <div className="bg-purple-800 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-white font-bold flex items-center gap-2">
                    <Library size={20} /> 📚 Shared Banner Library
                </h4>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-teal-600 hover:bg-teal-500 text-white text-sm px-3 py-2 rounded flex items-center gap-1"
                >
                    <Plus size={16} /> Add Sponsor
                </button>
            </div>

            <p className="text-purple-300 text-sm">
                Upload banners here once, then apply them to multiple quizzes. 
                Perfect for generic advertisers like Amazon, Google Ads, etc.
            </p>

            {/* Error Display */}
            {error && (
                <div className="bg-red-900 border border-red-600 text-red-200 px-4 py-2 rounded flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Add New Sponsor Form */}
            {showAddForm && (
                <div className="bg-purple-900 rounded-lg p-4 space-y-3">
                    <h5 className="text-teal-300 font-semibold">➕ Add New Sponsor</h5>
                    
                    <div>
                        <label className="text-purple-300 text-xs block mb-1">Sponsor Name:</label>
                        <input
                            type="text"
                            value={newSponsorName}
                            onChange={(e) => setNewSponsorName(e.target.value)}
                            placeholder="e.g., Amazon, NordVPN, Skillshare"
                            className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
                        />
                    </div>

                    <div>
                        <label className="text-purple-300 text-xs block mb-1">Affiliate URL:</label>
                        <input
                            type="url"
                            value={newAffiliateUrl}
                            onChange={(e) => setNewAffiliateUrl(e.target.value)}
                            placeholder="https://affiliate-link.com/?ref=YOUR_ID"
                            className="w-full bg-purple-700 text-white px-3 py-2 rounded text-sm"
                        />
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={handleAddSponsor}
                            className="bg-green-600 hover:bg-green-500 text-white text-sm px-4 py-2 rounded"
                        >
                            Create Sponsor
                        </button>
                        <button
                            onClick={() => setShowAddForm(false)}
                            className="bg-gray-600 hover:bg-gray-500 text-white text-sm px-4 py-2 rounded"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Sponsors List */}
            {loading ? (
                <p className="text-purple-400 text-center py-4">Loading sponsors...</p>
            ) : sponsors.length === 0 ? (
                <div className="text-center py-8">
                    <p className="text-purple-400 mb-2">No shared sponsors yet</p>
                    <p className="text-purple-500 text-sm">
                        Click "Add Sponsor" to create your first shared banner set
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {sponsors.map(sponsor => (
                        <div 
                            key={sponsor.sponsor}
                            className="bg-purple-900 rounded-lg p-4 border border-purple-700"
                        >
                            {/* Sponsor Header */}
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex-1">
                                    {/* Editable Name */}
                                    {editingName === sponsor.sponsor ? (
                                        <div className="flex gap-2 items-center mb-1">
                                            <input
                                                type="text"
                                                defaultValue={sponsor.displayName || sponsor.sponsor}
                                                id={`name-${sponsor.sponsor}`}
                                                className="bg-purple-700 text-white px-3 py-1 rounded text-lg font-bold"
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => {
                                                    const input = document.getElementById(`name-${sponsor.sponsor}`);
                                                    updateSponsorName(sponsor.sponsor, input.value);
                                                }}
                                                className="bg-green-600 hover:bg-green-500 text-white text-xs px-2 py-1 rounded"
                                            >
                                                <Save size={14} />
                                            </button>
                                            <button
                                                onClick={() => setEditingName(null)}
                                                className="bg-gray-600 hover:bg-gray-500 text-white text-xs px-2 py-1 rounded"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 mb-1">
                                            <h5 className="text-white font-bold text-lg">
                                                {sponsor.displayName || sponsor.sponsor}
                                            </h5>
                                            <button
                                                onClick={() => setEditingName(sponsor.sponsor)}
                                                className="text-purple-400 hover:text-purple-300"
                                                title="Edit name"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                        </div>
                                    )}
                                    <p className="text-purple-400 text-xs">
                                        Folder: _shared/{sponsor.sponsor}/
                                    </p>
                                </div>
                                
                                {/* Delete Sponsor Button */}
                                <button
                                    onClick={() => handleDeleteSponsor(sponsor.sponsor, sponsor.displayName || sponsor.sponsor)}
                                    disabled={deletingSponsor === sponsor.sponsor}
                                    className={`text-xs px-3 py-1 rounded flex items-center gap-1 ${
                                        deletingSponsor === sponsor.sponsor
                                            ? 'bg-gray-600 text-gray-400'
                                            : 'bg-red-700 hover:bg-red-600 text-white'
                                    }`}
                                    title="Delete this sponsor and all banners"
                                >
                                    {deletingSponsor === sponsor.sponsor ? (
                                        '⏳ Deleting...'
                                    ) : (
                                        <>
                                            <Trash2 size={14} /> Delete Sponsor
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Affiliate URL */}
                            <div className="mb-3">
                                <label className="text-purple-300 text-xs block mb-1">
                                    🔗 Affiliate URL:
                                </label>
                                {editingAffiliate === sponsor.sponsor ? (
                                    <div className="flex gap-2">
                                        <input
                                            type="url"
                                            defaultValue={sponsor.affiliateUrl}
                                            id={`affiliate-${sponsor.sponsor}`}
                                            className="flex-1 bg-purple-700 text-white px-3 py-1 rounded text-sm"
                                        />
                                        <button
                                            onClick={() => {
                                                const input = document.getElementById(`affiliate-${sponsor.sponsor}`);
                                                updateAffiliateUrl(sponsor.sponsor, input.value);
                                            }}
                                            className="bg-green-600 hover:bg-green-500 text-white text-xs px-2 py-1 rounded"
                                        >
                                            <Save size={14} />
                                        </button>
                                        <button
                                            onClick={() => setEditingAffiliate(null)}
                                            className="bg-gray-600 hover:bg-gray-500 text-white text-xs px-2 py-1 rounded"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2 items-center">
                                        <span className="text-teal-400 text-sm truncate flex-1">
                                            {sponsor.affiliateUrl || '(not set)'}
                                        </span>
                                        <button
                                            onClick={() => setEditingAffiliate(sponsor.sponsor)}
                                            className="text-purple-400 hover:text-purple-300 text-xs"
                                        >
                                            Edit
                                        </button>
                                        {sponsor.affiliateUrl && (
                                            <a
                                                href={sponsor.affiliateUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-purple-400 hover:text-purple-300"
                                            >
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Banner Upload Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                {BANNER_TYPES.map(type => {
                                    const uploadKey = `${sponsor.sponsor}-${type.key}`;
                                    const hasImage = sponsor.banners?.[type.key];
                                    const isUploading = uploading[uploadKey];
                                    const isDeleting = deleting[uploadKey];

                                    return (
                                        <div 
                                            key={type.key}
                                            className="bg-purple-800 rounded p-2"
                                        >
                                            <p className="text-purple-300 text-xs font-semibold mb-1">
                                                {type.label}
                                            </p>
                                            <p className="text-purple-500 text-xs mb-2">{type.size}</p>

                                            {hasImage ? (
                                                <div className="relative">
                                                    <img
                                                        src={sponsor.banners[type.key]}
                                                        alt={type.label}
                                                        className="w-full h-12 object-contain bg-purple-900 rounded"
                                                    />
                                                    <span className="absolute top-0 right-0 bg-green-600 text-white text-xs px-1 rounded-bl">
                                                        ✓
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="w-full h-12 bg-purple-700 rounded flex items-center justify-center">
                                                    <span className="text-purple-500 text-xs">Empty</span>
                                                </div>
                                            )}

                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={el => fileInputRefs.current[uploadKey] = el}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        handleUpload(sponsor.sponsor, type.key, file, uploadKey);
                                                    }
                                                }}
                                                className="hidden"
                                            />
                                            
                                            <div className="flex gap-1 mt-1">
                                                <button
                                                    onClick={() => fileInputRefs.current[uploadKey]?.click()}
                                                    disabled={isUploading}
                                                    className={`flex-1 text-xs py-1 rounded flex items-center justify-center gap-1 ${
                                                        isUploading
                                                            ? 'bg-gray-600 text-gray-400'
                                                            : 'bg-purple-600 hover:bg-purple-500 text-white'
                                                    }`}
                                                >
                                                    {isUploading ? (
                                                        '⏳'
                                                    ) : (
                                                        <>
                                                            <Upload size={12} /> 
                                                            {hasImage ? 'Replace' : 'Upload'}
                                                        </>
                                                    )}
                                                </button>
                                                
                                                {hasImage && (
                                                    <button
                                                        onClick={() => handleDeleteBanner(sponsor.sponsor, type.key)}
                                                        disabled={isDeleting}
                                                        className={`text-xs py-1 px-2 rounded ${
                                                            isDeleting
                                                                ? 'bg-gray-600 text-gray-400'
                                                                : 'bg-red-600 hover:bg-red-500 text-white'
                                                        }`}
                                                        title="Delete banner"
                                                    >
                                                        {isDeleting ? '⏳' : <Trash2 size={12} />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Refresh Button */}
            <button
                onClick={loadSponsors}
                className="text-purple-400 hover:text-purple-300 text-sm"
            >
                🔄 Refresh sponsor list
            </button>
        </div>
    );
};

export default SharedBannerManager;
